/**
 * "How much dealer info have we actually collected, and what is still outstanding?"
 *
 * One card per sector. Each card shows the seven things we try to hold on a dealer, how
 * many dealers have each, and -- separately -- how many a human has signed off. Collected
 * and verified are deliberately two different numbers: the enrichment job can find a
 * business on Google, but only a person can say it found the *right* one.
 *
 * Clicking a sector card filters the table below it, so "38 missing a logo" is one click
 * away from the list of the 38.
 */
import React, { useEffect, useState } from 'react';
import { fetchData } from '../../../services/api';
import { ApiURLs } from '../../../constant/ApiUrls';

const SECTOR_COLOURS = {
  Motors: '#1b87f4',
  Property: '#00a651',
  Jobs: '#9b51e0',
  'Agri Services': '#f2994a',
  Unassigned: '#98a2b3',
};

const barColour = (pct) => (pct >= 90 ? '#00a651' : pct >= 60 ? '#f2c94c' : '#eb5757');

const cardStyle = (active, colour) => ({
  flex: '1 1 260px',
  minWidth: 240,
  border: `1px solid ${active ? colour : '#e4e7ec'}`,
  boxShadow: active ? `0 0 0 2px ${colour}22` : 'none',
  borderRadius: 10,
  padding: '12px 14px',
  background: '#fff',
  cursor: 'pointer',
  transition: 'box-shadow .15s, border-color .15s',
});

const DealerDataPanel = ({ activeSector, onPickSector, refreshKey }) => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData(ApiURLs.DEALER_DATA_SUMMARY);
        if (cancelled) return;
        if (res?.status === 1) setSummary(res.data);
        else setError('Could not load the dealer data summary');
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load the dealer data summary');
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (error) {
    return <div style={{ padding: '10px 14px', color: '#eb5757', fontSize: 13 }}>{error}</div>;
  }
  if (!summary) {
    return <div style={{ padding: '10px 14px', color: '#98a2b3', fontSize: 13 }}>Loading dealer data summary…</div>;
  }

  const { sectors = [], totals = {} } = summary;
  const outstanding = (totals.dealers || 0) - (totals.verified || 0);

  return (
    <div style={{ padding: '12px 10px 4px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#101828' }}>Dealer info collected</h3>
        <span style={{ fontSize: 13, color: '#667085' }}>
          {totals.verified || 0} of {totals.dealers || 0} verified
          {outstanding > 0 && (
            <strong style={{ color: '#eb5757' }}> · {outstanding} still to check</strong>
          )}
          {totals.pending_suggestions > 0 && (
            <strong style={{ color: '#f2994a' }}> · {totals.pending_suggestions} Google matches awaiting approval</strong>
          )}
        </span>
        {activeSector && (
          <button
            onClick={() => onPickSector('')}
            style={{
              marginLeft: 'auto', border: '1px solid #d0d5dd', background: '#fff',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#475467',
            }}
          >
            Showing {activeSector} — show all sectors
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {sectors.map((s) => {
          const colour = SECTOR_COLOURS[s.sector] || '#667085';
          const active = activeSector === s.sector;
          return (
            <div
              key={s.sector}
              style={cardStyle(active, colour)}
              onClick={() => onPickSector(active ? '' : s.sector)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onPickSector(active ? '' : s.sector); }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 14, color: colour }}>{s.sector}</span>
                <span style={{ fontSize: 12, color: '#667085' }}>{s.dealers} dealers</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 10px' }}>
                <div style={{ flex: 1, height: 6, background: '#f2f4f7', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${s.completeness}%`, height: '100%', background: barColour(s.completeness) }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#344054' }}>{s.completeness}%</span>
              </div>

              {/* The point of the card: not what we have, but what is missing. */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {s.fields.map((f) => (
                  <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#667085' }}>{f.label}</span>
                    <span style={{ color: f.missing ? '#eb5757' : '#00a651', fontWeight: 600 }}>
                      {f.missing ? `${f.missing} missing` : 'all in'}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 9, paddingTop: 8, borderTop: '1px dashed #e4e7ec', fontSize: 12, color: '#475467' }}>
                Verified by hand: <strong>{s.verified}/{s.dealers}</strong>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DealerDataPanel;
